/**
 * AchievementContext — watches student progress and raises congratulation
 * pop-ups when new badges / certificates are earned.
 *
 * Persistence of "already celebrated" keys:
 *   localStorage: ppx_ach_seen_{uid}  (instant)
 *   Firestore:    users/{uid}  field  achievementsSeen: string[]
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { doc, getDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useEnrollment } from '../utils/useEnrollment';
import { useAllProgressState } from '../utils/useProgress';
import {
  earnedChapterBadges,
  earnedSubjectCertificates,
  earnedProgramTrophies,
  achievementKey,
  resolveSubjectLabel,
} from '../utils/achievements';
import AchievementModal from '../components/achievement/AchievementModal';

const AchievementContext = createContext({ recheck: () => {} });

function lsKey(uid) { return `ppx_ach_seen_${uid}`; }
function readSeen(uid) {
  try { return new Set(JSON.parse(localStorage.getItem(lsKey(uid)) || '[]')); }
  catch { return new Set(); }
}
function writeSeen(uid, set) {
  try { localStorage.setItem(lsKey(uid), JSON.stringify([...set])); } catch { /* full */ }
}

export function AchievementProvider({ children }) {
  const { currentUser, userData } = useAuth();
  const { enrolled } = useEnrollment();
  const { allProgress, loading: progressLoading } = useAllProgressState();
  const [queue, setQueue] = useState([]);
  const seenRef = useRef(new Set());
  const [loadedUid, setLoadedUid] = useState(null);
  const prevKeysRef = useRef(null); // null = baseline not yet captured

  // Load seen keys on login
  useEffect(() => {
    let cancelled = false;
    setQueue([]);
    prevKeysRef.current = null;
    setLoadedUid(null);
    if (!currentUser) { seenRef.current = new Set(); return; }
    seenRef.current = readSeen(currentUser.uid);
    getDoc(doc(db, 'users', currentUser.uid))
      .then(snap => {
        if (cancelled) return;
        const fsSeen = snap.data()?.achievementsSeen || [];
        fsSeen.forEach(k => seenRef.current.add(k));
        writeSeen(currentUser.uid, seenRef.current);
        setLoadedUid(currentUser.uid);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('[AchievementProvider] Could not load celebrated achievements:', error);
        setLoadedUid(currentUser.uid);
      });
    return () => { cancelled = true; };
  }, [currentUser?.uid]); // eslint-disable-line

  // Diff current achievements vs seen → queue pop-ups for NEW ones
  const recheck = useCallback(() => {
    if (!currentUser || loadedUid !== currentUser.uid || progressLoading) return;
    const badges = earnedChapterBadges(allProgress);
    const certs = earnedSubjectCertificates(allProgress);
    const trophies = earnedProgramTrophies(allProgress, enrolled);
    const all = [...badges, ...certs, ...trophies];
    const currentKeys = new Set(all.map(achievementKey));

    // First pass after load: mark everything already earned as the baseline.
    // Wait for every progress snapshot, including empty documents for new students.
    if (prevKeysRef.current === null) {
      prevKeysRef.current = currentKeys;
      return;
    }

    // Celebrate only achievements that are BOTH newly-earned this session AND
    // never celebrated before (persisted). So a chapter pops exactly once, ever —
    // the moment it first completes — and never again on later page opens.
    const fresh = all.filter(a => {
      const k = achievementKey(a);
      return !prevKeysRef.current.has(k) && !seenRef.current.has(k);
    });
    prevKeysRef.current = currentKeys;
    if (fresh.length === 0) return;

    const newKeys = fresh.map(achievementKey);
    newKeys.forEach(k => seenRef.current.add(k));
    writeSeen(currentUser.uid, seenRef.current);
    setDoc(
      doc(db, 'users', currentUser.uid),
      { achievementsSeen: arrayUnion(...newKeys) },
      { merge: true }
    ).catch(error => console.error('[AchievementProvider] Could not save celebrated achievements:', error));

    // Merge a chapter badge + its subject certificate into a single pop-up
    const consumedCerts = new Set();
    const merged = [];
    fresh.forEach(a => {
      if (a.type === 'chapter') {
        const cert = fresh.find(c => c.type === 'subject' && c.subjectKey === a.subjectKey);
        if (cert) {
          consumedCerts.add(achievementKey(cert));
          const title = resolveSubjectLabel(cert.subjectKey, userData?.preparingFor);
          merged.push({ achievement: a, certificate: { title, subtitle: `All ${cert.badgeCount} chapters completed` } });
          return;
        }
      }
      merged.push({ achievement: a });
    });
    const finalItems = merged.filter(
      i => !(i.achievement.type === 'subject' && consumedCerts.has(achievementKey(i.achievement)))
    );
    setQueue(q => [...q, ...finalItems]);
  }, [currentUser, allProgress, enrolled, userData, loadedUid, progressLoading]);

  // Recheck whenever progress data changes
  const progressFingerprint = useMemo(() => JSON.stringify(allProgress), [allProgress]);
  useEffect(() => { recheck(); }, [progressFingerprint, recheck]);

  const studentName = userData?.username || currentUser?.displayName || '';
  const preparingFor = userData?.preparingFor || '';
  const current = queue[0] || null;
  const dismiss = () => setQueue(q => q.slice(1));

  return (
    <AchievementContext.Provider value={{ recheck }}>
      {children}
      {current && (
        <AchievementModal
          achievement={current.achievement}
          certificate={current.certificate}
          studentName={studentName}
          preparingFor={preparingFor}
          onClose={dismiss}
        />
      )}
    </AchievementContext.Provider>
  );
}

export function useAchievements() {
  return useContext(AchievementContext);
}
